// @ts-check

/**
 * @file Renders the Expo Router not-found route.
 */
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {Link} from "expo-router"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {Text, View} from "react-native"
import ScreenTitle from "../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} NotFoundScreenProps */

/** @typedef {Record<string, never>} NotFoundScreenState */

/**
 * Fallback screen rendered when expo-router cannot match the current URL.
 * @augments {ShapeComponent<NotFoundScreenProps, NotFoundScreenState>}
 */
class NotFoundScreen extends ShapeComponent {
  /** Initializes translations for the fallback route. */
  setup() {
    useLocale()
  }

  /**
   * Renders the not-found screen.
   * @returns {ReactNode} Not-found route content.
   */
  render() {
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="notFoundScreen"
      >
        <ScreenTitle>{_("Page not found")}</ScreenTitle>
        <View
          style={styles.content ||= {
            alignItems: "center",
            padding: 48
          }}
          testID="notFoundScreen/content"
        >
          <Text
            style={styles.code ||= {
              color: "#1d4ed8",
              fontSize: 64,
              fontWeight: 700,
              letterSpacing: -1
            }}
            testID="notFoundScreen/code"
          >
            {"404"}
          </Text>
          <Text
            style={styles.title ||= {
              color: "#0f172a",
              fontSize: 24,
              fontWeight: 700,
              marginTop: 8
            }}
            testID="notFoundScreen/title"
          >
            {_("Page not found")}
          </Text>
          <Text
            style={styles.subtitle ||= {
              color: "#64748b",
              fontSize: 14,
              fontWeight: 500,
              marginBottom: 24,
              marginTop: 8,
              textAlign: "center"
            }}
            testID="notFoundScreen/subtitle"
          >
            {_("The page you were looking for does not exist or has moved.")}
          </Text>
          <Link
            href="/"
            style={styles.link ||= {
              backgroundColor: "#1d4ed8",
              borderRadius: 10,
              paddingHorizontal: 16,
              paddingVertical: 10
            }}
            testID="notFoundScreen/homeLink"
          >
            <Text
              style={styles.linkText ||= {
                color: "#ffffff",
                fontSize: 14,
                fontWeight: 700
              }}
              testID="notFoundScreen/homeLink/text"
            >
              {_("Back to home")}
            </Text>
          </Link>
        </View>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<NotFoundScreenProps>} */
const NotFoundScreenComponent = memo(shapeComponent(NotFoundScreen))

export default NotFoundScreenComponent
